import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { Spinner } from "@fluentui/react/lib/Spinner";

import LogoImage from "../../components/logo-image-ici/LogoImage";
import { TEXT } from "../../shared/constants/texts";
import { ROUTE } from "../../shared/constants/routes";
import { loginFailed, loginSuccess, startAuthProcess, stopAuthProcess, resetLoginState } from "./redux/slice";
import {
  validateTokenApiCall,
  getIdpTokenApiCall,
  getIdpTokenFromRefreshTokenApiCall,
  getIcmTokenFromRefreshTokenApiCall,
} from "./redux/apiCalls";

import ConfigService from "../../shared/services/configService";
import SessionService from "../../shared/services/sessionService";
import { logError } from "../../shared/services/errorLogging";
const configService = new ConfigService();
const sessionService = new SessionService();

let timeOutforRefreshToken = null;
let timeOutforRefreshTokenIcm = null;

const Login: React.FC = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const state = useSelector((state: any) => state.loginSlice);
  const { isAuthInProgress, isAuthFailed, userData, isAuthDone } = state;
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));

  React.useEffect(() => {
    // @ts-ignore
    if (props && props.location && props.location.state && props.location.state.fromLogout) {
      dispatch(resetLoginState());
      onLoginClickedHandler();
    } else {
      dispatch(startAuthProcess());
      checkIfUserAlreadyLoggedIn();
    }
  }, []);

  React.useEffect(() => {
    if (userData && isAuthDone && !isAuthFailed) {
      if (Office.context.mailbox.item.displayReplyForm != undefined) {
        history.push(ROUTE.ADD_TO_ICI);
      } else {
        history.push(ROUTE.ATTACH_FROM_ICI);
      }
    }
  }, [userData, isAuthDone, isAuthFailed]);

  const checkIfUserAlreadyLoggedIn = async () => {
    const isIdpEnabled = configService.getOauthFlag().toString();
    if (isIdpEnabled === "false") {
      let token = sessionService.getAuthToken();
      let refreshToken = sessionService.getIcmRefreshToken();
      let expiresIn = sessionService.getIcmExpiresIn();
      if (token) {
        await validateToken(token);
        await callGetTokenfromRefreshTokenIcm(token, refreshToken, expiresIn);
        token = sessionService.getAuthToken();
        refreshToken = sessionService.getIcmRefreshToken();
        expiresIn = sessionService.getIcmExpiresIn();
        dispatch(loginSuccess({ token: token, userData: { token, expiresIn, refreshToken } }));
      } else {
        dispatch(stopAuthProcess());
      }
    } else {
      let idpToken = sessionService.getIdpAccessToken();
      let expiresIn = sessionService.getIdpExpiresIn();
      let refreshToken = sessionService.getIdpRefreshToken();
      let id_Token = sessionService.getIdpIdToken();
      if (idpToken) {
        await callGetTokenfromRefreshToken(refreshToken);
        idpToken = sessionService.getIdpAccessToken();
        expiresIn = sessionService.getIdpExpiresIn();
        refreshToken = sessionService.getIdpRefreshToken();
        id_Token = sessionService.getIdpIdToken();
        dispatch(loginSuccess({ token: idpToken, userData: { idpToken, expiresIn, refreshToken, id_Token } }));
      } else {
        const code = sessionService.getAuthCode();
        if (code) {
          await getIdpToken({ code });
        } else {
          dispatch(stopAuthProcess());
        }
      }
    }
  };

  const onLoginClickedHandler = async () => {
    dispatch(startAuthProcess());
    history.push(ROUTE.LOGIN);
    await configService.getTaskpaneConfigData();
    const userApiTokenUrl = configService.getUserAPITokenURl();
    const isIdpEnabled = configService.getOauthFlag().toString();
    let dialog,
      authUrl = "";
    if (isIdpEnabled === "false") {
      authUrl = userApiTokenUrl;
    } else {
      authUrl = window.location.origin + "/login.html";
    }
    Office.context.ui.displayDialogAsync(authUrl, { height: 70, width: 40, promptBeforeOpen: false }, (asyncResult) => {
      if (asyncResult && asyncResult.value) {
        dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (response) => addEventHandler(response, dialog));
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (response) => addEventHandler(response, dialog));
      } else {
        if (asyncResult.error) {
          console.log("error in async result login clicked handler:", asyncResult.error);
        }
        dispatch(loginFailed());
        dialogCloseAsync(dialog, asyncResult);
      }
    });
  };

  const addEventHandler = (response, dialog) => {
    const isIdpEnabled = configService.getOauthFlag().toString();
    if (response.type !== Office.EventType.DialogEventReceived) {
      if (isIdpEnabled === "false") {
        dialogCloseAsync(dialog, icmAuthTokenHandler(response));
      } else {
        dialogCloseAsync(dialog, idpTokenHandler(response));
      }
    } else {
      if (response.error) {
        dispatch(loginFailed(response.error));
        dialogCloseAsync(dialog, response.error);
      } else {
        if (isIdpEnabled === "false") {
          icmAuthTokenHandler(response.message);
        } else {
          idpTokenHandler(response.message);
        }
      }
    }
  };

  const idpTokenHandler = async (response) => {
    const data = JSON.parse(response.message);
    sessionService.setAuthCode(data.message);
    dispatch(startAuthProcess());
    await checkIfUserAlreadyLoggedIn();
    dispatch(loginSuccess({ token: data.message, userData: JSON.parse(response.message).message }));
  };

  const icmAuthTokenHandler = async (response) => {
    const data = JSON.parse(response.message);
    const { token, ici_refreshtoken, ici_accesstokenTTL } = data;
    setIcmData({ token: token, refreshToken: ici_refreshtoken, expiresIn: ici_accesstokenTTL });
    dispatch(startAuthProcess());
    await checkIfUserAlreadyLoggedIn();
    dispatch(loginSuccess({ token: data.token, userData: JSON.parse(response.message).token }));
  };

  const validateToken = async (data) => {
    const token = data.replace('"', "").replace('"', "");
    const validateTokenResponse = await validateTokenApiCall({
      dispatch: dispatch,
      history: history,
      requestData: token,
    });
    const { response, error } = validateTokenResponse;
    if (response) {
      localStorage.setItem(TEXT.USER_EMAIL_ID, response.EmailID);
      sessionService.setAuthToken(token);
      dispatch(loginSuccess({ token: token, userData: response }));
    }
    if (error) {
      dispatch(loginFailed());
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|login|index - validateToken",
          callerApi: "User/ValidateUserApiToken",
        },
      });
      sessionService.resetSession();
    }
  };

  const getIdpToken = async ({ code }) => {
    const getIdpTokenResponse = await getIdpTokenApiCall({
      dispatch: dispatch,
      history: history,
      requestData: {
        AuthCode: code,
      },
    });
    const { response, error } = getIdpTokenResponse;
    if (response) {
      setIdpData(response);
    }
    if (error) {
      dispatch(loginFailed());
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|login|index - getIdpToken",
          callerApi: "/Auth/ReturnAuthToken",
        },
      });
      sessionService.resetSession();
    }
  };

  const callGetTokenfromRefreshToken = async (refreshToken) => {
    const getIdpTokenResponse = await getIdpTokenFromRefreshTokenApiCall({
      dispatch: dispatch,
      history: history,
      requestData: {
        RefreshToken: refreshToken,
      },
    });
    const { response, error } = getIdpTokenResponse;
    if (response) {
      setIdpData(response);
    }
    if (error) {
      dispatch(loginFailed());
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|login|index - callGetTokenfromRefreshToken",
          callerApi: "/Auth/ReturnAuthToken",
        },
      });
      sessionService.resetSession();
    }
  };

  const setIdpData = (data) => {
    const { accessToken, expiresIn, refreshToken, id_Token } = data;
    if (accessToken) {
      sessionService.setIdpAccessToken(accessToken);
    }
    if (expiresIn) {
      sessionService.setIdpExpiresIn(expiresIn);
    }
    if (id_Token) {
      sessionService.setIdpIdToken(id_Token);
    }
    if (refreshToken) {
      sessionService.setIdpRefreshToken(refreshToken);
    }
    clearTimeout(timeOutforRefreshToken);
    timeOutforRefreshToken = setTimeout(() => callGetTokenfromRefreshToken(refreshToken), expiresIn * 0.8 * 1000);
    dispatch(loginSuccess({ token: accessToken, userData: { accessToken, expiresIn, refreshToken, id_Token } }));
  };

  const setIcmData = (data) => {
    const { token, refreshToken, expiresIn } = data;
    if (token) {
      sessionService.setAuthToken(token.replace('"', "").replace('"', ""));
    }
    if (expiresIn) {
      sessionService.setIcmExpiresIn(expiresIn);
    }
    if (refreshToken) {
      sessionService.setIcmRefreshToken(refreshToken.replace('"', "").replace('"', ""));
    }
    clearTimeout(timeOutforRefreshTokenIcm);
    timeOutforRefreshTokenIcm = setTimeout(
      () => {
        callGetTokenfromRefreshTokenIcm(token, refreshToken, expiresIn);
      },
      Number(expiresIn) * 0.8 * 1000
    );
    dispatch(loginSuccess({ token: token, userData: { token, expiresIn, refreshToken } }));
  };

  const callGetTokenfromRefreshTokenIcm = async (token, refreshToken, expiresIn) => {
    const getIcmTokenResponse = await getIcmTokenFromRefreshTokenApiCall({
      dispatch: dispatch,
      history: history,
      requestData: {
        refreshToken: refreshToken,
        token: token,
      },
    });
    const { response, error } = getIcmTokenResponse;
    if (response) {
      setIcmData({
        token: response.ICMAuthToken,
        refreshToken: response.ICIRefreshToken,
        expiresIn: expiresIn,
      });
    }
    if (error) {
      dispatch(loginFailed());
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|login|index - callGetTokenfromRefreshToken",
          callerApi: "/api/AuthToken/RefreshToken",
        },
      });
      sessionService.resetSession();
    }
  };

  const imgSrc = "assets/images/icertis-logo/icertis-logo-horiz-on-white-60px.png";

  return (
    <div className="login-container">
      <div>
        <LogoImage logoImage={imgSrc} />
      </div>
      <div className="err-msg-login-button-container">
        {!isAuthInProgress ? (
          <div>
            {isAuthFailed ? (
              <p className="err-msg-login">{errorValidationMsg && errorValidationMsg.LOGIN_FAILED}</p>
            ) : null}
            <div className="login-button-container">
              <DefaultButton title={TEXT.LOGIN} target="_blank" allowDisabledFocus onClick={onLoginClickedHandler}>
                {TEXT.LOGIN}
              </DefaultButton>
            </div>
          </div>
        ) : (
          <div className="spinner-container">
            <Spinner size={3} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;

export const dialogCloseAsync = (dialog, _asyncResult) => {
  dialog.close();
  setTimeout(function () {
    try {
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, function () {});
    } catch (e) {
      console.log("error in dialogCloseAsync: ", e.Message);
    }
  }, 0);
};
